import { supabase } from './supabase';
import { generateFlowerProperties } from '../config/flowers';

// ============================================
// AUTH FUNCTIONS
// ============================================

export async function signInWithEmail(email, marketingConsent = false) {
  const { data, error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: true,
      data: {
        marketing_consent: marketingConsent
      }
    }
  });
  
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

// ============================================
// GARDEN FUNCTIONS
// ============================================

export async function getOrCreateGarden(userId) {
  const { data: existingGarden } = await supabase
    .from('gardens')
    .select('*')
    .eq('user_id', userId)
    .single();
  
  if (existingGarden) {
    return existingGarden;
  }
  
  // Get user metadata to check marketing consent
  const { data: { user } } = await supabase.auth.getUser();
  
  const { data: newGarden, error: createError } = await supabase
    .from('gardens')
    .insert([
      { 
        user_id: userId,
        baby_name: null,
        baby_birthdate: null,
        marketing_consent: user?.user_metadata?.marketing_consent || false
      }
    ])
    .select()
    .single();
  
  if (createError) throw createError;
  return newGarden;
}

export async function updateGarden(gardenId, updates) {
  const { data, error } = await supabase
    .from('gardens')
    .update(updates)
    .eq('id', gardenId)
    .select()
    .single();
  
  if (error) throw error;
  return data;
}

// ============================================
// WORD FUNCTIONS
// ============================================

export async function getWords(gardenId) {
  const { data, error } = await supabase
    .from('words')
    .select('*')
    .eq('garden_id', gardenId)
    .order('word_order', { ascending: true });
  
  if (error) throw error;
  return data || [];
}

export async function addWord(gardenId, wordText, wordDate, wordOrder) {
  const isFirstWord = wordOrder === 0;
  const flowerProps = generateFlowerProperties(isFirstWord);
  
  const { data, error } = await supabase
    .from('words')
    .insert([
      {
        garden_id: gardenId,
        text: wordText,
        date: wordDate,
        word_order: wordOrder,
        ...flowerProps
      }
    ])
    .select()
    .single();
  
  if (error) throw error;
  return data;
}

export async function deleteWord(wordId) {
  const { error } = await supabase
    .from('words')
    .delete()
    .eq('id', wordId);
  
  if (error) throw error;
}

export async function rearrangeWords(gardenId) {
  const words = await getWords(gardenId);
  
  const updates = words.map(word => {
    const newProps = generateFlowerProperties(word.word_order === 0);
    return {
      id: word.id,
      ...newProps
    };
  });
  
  const promises = updates.map(update => 
    supabase
      .from('words')
      .update(update)
      .eq('id', update.id)
  );
  
  await Promise.all(promises);
  
  return await getWords(gardenId);
}

// ============================================
// PAYMENT FUNCTIONS
// ============================================

export async function checkPaymentStatus(userId) {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'paid')
    .single();
  
  if (error && error.code !== 'PGRST116') {
    throw error;
  }
  
  return data || null;
}

export async function recordPayment(userId, paymentDetails) {
  const { data, error } = await supabase
    .from('payments')
    .insert([
      {
        user_id: userId,
        order_id: paymentDetails.order_id,
        variant_id: paymentDetails.variant_id,
        customer_email: paymentDetails.customer_email,
        status: 'paid',
        amount: paymentDetails.amount,
        currency: paymentDetails.currency || 'USD',
        paid_at: new Date().toISOString()
      }
    ])
    .select()
    .single();
  
  if (error) throw error;
  return data;
}